import cls from "@fvilers/cls";
import { TriangleIcon } from "@imi/icons/TriangleIcon.js";
import { Badge } from "@imi/ui/components/Badge/Badge.js";
import { Tooltip } from "@imi/ui/components/Tooltip/Tooltip.js";
import { TooltipContent } from "@imi/ui/components/Tooltip/TooltipContent.js";
import { TooltipTrigger } from "@imi/ui/components/Tooltip/TooltipTrigger.js";
import { useMediaQuery } from "@imi/ui/hooks/useMediaQuery.js";
import {
  getSuggestionStatus,
  SuggestionStatus,
} from "@src/core/engine/technicalSelection/types";
import { ReactNode } from "react";
import { TechnicalSelectionOutput } from "../hooks/useBuildSuggestionOutput";
import styles from "./OutputField.module.css";

type OutputFieldProps = {
  alignTooltip?: "center" | "end" | "start";
  className?: string;
  output: TechnicalSelectionOutput | undefined;
};

const statusIconMap: Record<SuggestionStatus, ReactNode> = {
  error: <TriangleIcon />,
  info: <Badge>Info</Badge>,
  ok: null,
  warning: <TriangleIcon />,
};

export function OutputField({
  alignTooltip,
  className,
  output,
}: OutputFieldProps) {
  const isMobileXs = useMediaQuery("mobileXs");
  if (!output) return null;
  const status = getSuggestionStatus(output.status);
  return output.error && status && status !== "ok" && status !== "info" ? (
    <Tooltip>
      <TooltipTrigger asChild>
        <span
          className={cls(styles.OutputField, className)}
          data-status={status}
        >
          <span data-testid="output-value">{output.formattedValue}</span>
          {statusIconMap[status]}
        </span>
      </TooltipTrigger>
      <TooltipContent
        align={alignTooltip ?? (isMobileXs ? "center" : "end")}
        alignOffset={isMobileXs || alignTooltip === "center" ? 0 : -4}
        className={styles.TooltipContent}
        variant={status}
      >
        {output.error}
      </TooltipContent>
    </Tooltip>
  ) : (
    <span className={cls(styles.OutputField, className)} data-status={status}>
      <span data-testid="output-value">{output.formattedValue}</span>
      {status === "info" && <span className={styles.Info}>{output.error}</span>}
    </span>
  );
}
